/**
 * Markdown component definitions.
 *
 * Custom React components for rendering markdown content in grammar lessons.
 * Provides styled components for headings, paragraphs, lists, code blocks,
 * and other markdown elements with consistent styling.
 *
 * @module MarkdownComponents
 */

import React from 'react';

// Markdown component definitions for grammar content rendering
export function MarkdownH1({ children }: Readonly<{ children: React.ReactNode }>) {
  return <h1 className="text-2xl font-bold mb-4 text-primary">{children}</h1>;
}

export function MarkdownH2({ children }: Readonly<{ children: React.ReactNode }>) {
  return <h2 className="text-xl font-semibold mb-3 mt-6 text-primary">{children}</h2>;
}

export function MarkdownH3({ children }: Readonly<{ children: React.ReactNode }>) {
  return <h3 className="text-lg font-semibold mb-2 mt-4">{children}</h3>;
}

export function MarkdownP({ children }: Readonly<{ children: React.ReactNode }>) {
  return <p className="mb-4 leading-relaxed">{children}</p>;
}

export function MarkdownUl({ children }: Readonly<{ children: React.ReactNode }>) {
  return <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>;
}

export function MarkdownOl({ children }: Readonly<{ children: React.ReactNode }>) {
  return <ol className="list-decimal pl-6 mb-4 space-y-1">{children}</ol>;
}

export function MarkdownLi({ children }: Readonly<{ children: React.ReactNode }>) {
  return <li className="leading-relaxed">{children}</li>;
}

export function MarkdownTable({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="overflow-x-auto mb-6 rounded-md shadow-sm">
      <table className="min-w-full border border-border rounded-md">{children}</table>
    </div>
  );
}

export function MarkdownThead({ children }: Readonly<{ children: React.ReactNode }>) {
  return <thead className="bg-primary/10 text-primary-foreground">{children}</thead>;
}

export function MarkdownTh({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <th className="border border-border px-4 py-3 text-left font-semibold text-primary">
      {children}
    </th>
  );
}

export function MarkdownTd({ children }: Readonly<{ children: React.ReactNode }>) {
  return <td className="border border-border px-4 py-3 align-middle">{children}</td>;
}

export function MarkdownBlockquote({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <blockquote className="border-l-4 border-primary/30 pl-4 italic my-4 bg-muted/30 py-2 rounded-r">
      {children}
    </blockquote>
  );
}

export function MarkdownCode({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  const isInline = !className;
  if (isInline) {
    return (
      <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-primary">
        {children}
      </code>
    );
  }
  return (
    <code className="block bg-muted p-4 rounded-lg overflow-x-auto font-mono text-sm">
      {children}
    </code>
  );
}

export function MarkdownEm({ children }: Readonly<{ children: React.ReactNode }>) {
  return <em className="text-primary/80 not-italic font-medium">{children}</em>;
}

export function MarkdownStrong({ children }: Readonly<{ children: React.ReactNode }>) {
  return <strong className="font-bold text-primary">{children}</strong>;
}

export function MarkdownHr() {
  return <hr className="my-8 border-border" />;
}

// Markdown components configuration
export const markdownComponents = {
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  p: MarkdownP,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  table: MarkdownTable,
  thead: MarkdownThead,
  th: MarkdownTh,
  td: MarkdownTd,
  blockquote: MarkdownBlockquote,
  code: MarkdownCode,
  em: MarkdownEm,
  strong: MarkdownStrong,
  hr: MarkdownHr,
};

